/**
 * Transforms an image locally using Canvas API
 * @param imageUrl - The URL of the full resolution image
 * @param options - Transformation options
 * @returns Promise resolving to the transformed image as a data URL
 */
export const transformImage = async (
  imageUrl: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    resize?: 'cover' | 'contain';
  } = {}
): Promise<string> => {
  const {
    width = 300,
    height = 200,
    quality = 0.65,
    resize = 'cover'
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Enable CORS for cross-origin images

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        canvas.width = width;
        canvas.height = height;

        // Calculate dimensions based on resize mode
        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = img.width;
        let sourceHeight = img.height;
        let destX = 0;
        let destY = 0;
        let destWidth = width;
        let destHeight = height;

        if (resize === 'cover') {
          // Cover mode: crop to fill the entire canvas
          const imageAspectRatio = img.width / img.height;
          const canvasAspectRatio = width / height;

          if (imageAspectRatio > canvasAspectRatio) {
            // Image is wider than canvas - crop horizontally
            sourceWidth = img.height * canvasAspectRatio;
            sourceHeight = img.height;
            sourceX = (img.width - sourceWidth) / 2;
            sourceY = 0;
          } else {
            // Image is taller than canvas - crop vertically
            sourceWidth = img.width;
            sourceHeight = img.width / canvasAspectRatio;
            sourceX = 0;
            sourceY = (img.height - sourceHeight) / 2;
          }
        } else {
          // Contain mode: fit entire image within canvas
          const imageAspectRatio = img.width / img.height;
          const canvasAspectRatio = width / height;

          if (imageAspectRatio > canvasAspectRatio) {
            // Image is wider - fit to width
            destWidth = width;
            destHeight = width / imageAspectRatio;
            destX = 0;
            destY = (height - destHeight) / 2;
          } else {
            // Image is taller - fit to height
            destWidth = height * imageAspectRatio;
            destHeight = height;
            destX = (width - destWidth) / 2;
            destY = 0;
          }
        }

        // Draw the image with calculated dimensions
        ctx.drawImage(
          img,
          sourceX, sourceY, sourceWidth, sourceHeight,
          destX, destY, destWidth, destHeight
        );

        // Convert to data URL with quality setting
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
};

/**
 * Preloads an image and returns a promise
 * @param imageUrl - The URL of the image to preload
 * @returns Promise resolving when image is loaded
 */
export const preloadImage = (imageUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Failed to load image'));

    img.src = imageUrl;
  });
};
